import { sanitizeUrl } from "../private/sanitizeUrl";
import type { FirstLoadTimingMetrics } from "../types";
import type { XviewPerfTimingRegistry } from "./firstApiTiming";

// Sanitize happens HERE, at the wire boundary — never where the registry is
// written. `firstApiRequestUrl` doubles as the match key that back-fills
// `response_end` from the PerformanceObserver entry (firstApiTiming.ts), and
// `excludedInstrumentationScriptUrls` is matched against raw `entry.name` in
// firstScriptEval.ts / observeResourceTimings. Normalizing either at store
// time silently breaks those lookups.

export function buildFirstLoadTiming(
  ns: XviewPerfTimingRegistry | undefined,
): FirstLoadTimingMetrics | undefined {
  if (ns == null) return undefined;

  const result: FirstLoadTimingMetrics = {};

  if (
    ns.firstApiRequestStartTime !== undefined &&
    ns.firstApiRequestDetectionMethod !== undefined &&
    ns.firstApiRequestUrl !== undefined
  ) {
    result.first_api_request = {
      start_time: ns.firstApiRequestStartTime,
      detection_method: ns.firstApiRequestDetectionMethod,
      url: sanitizeUrl(ns.firstApiRequestUrl, "api"),
      ...(ns.firstApiRequestResponseEnd !== undefined
        ? { response_end: ns.firstApiRequestResponseEnd }
        : {}),
    };
  }

  if (
    ns.firstScriptUrl !== undefined &&
    ns.firstScriptStartTime !== undefined &&
    ns.firstScriptResponseEnd !== undefined
  ) {
    result.first_script_resource = {
      url: ns.firstScriptUrl,
      start_time: ns.firstScriptStartTime,
      response_end: ns.firstScriptResponseEnd,
    };
  }

  if (
    ns.entryScriptResourceUrl !== undefined &&
    ns.entryScriptResourceStartTime !== undefined &&
    ns.entryScriptResourceResponseEnd !== undefined
  ) {
    result.entry_script_resource = {
      url: ns.entryScriptResourceUrl,
      start_time: ns.entryScriptResourceStartTime,
      response_end: ns.entryScriptResourceResponseEnd,
    };
  }

  if (
    ns.appMainScriptResourceUrl !== undefined &&
    ns.appMainScriptResourceStartTime !== undefined &&
    ns.appMainScriptResourceResponseEnd !== undefined
  ) {
    result.app_main_script_resource = {
      url: ns.appMainScriptResourceUrl,
      start_time: ns.appMainScriptResourceStartTime,
      response_end: ns.appMainScriptResourceResponseEnd,
    };
  }

  if (
    ns.excludedInstrumentationScriptUrls !== undefined &&
    ns.excludedInstrumentationScriptUrls.length > 0
  ) {
    result.excluded_instrumentation_script_urls = [
      ...ns.excludedInstrumentationScriptUrls,
    ];
  }

  if (Object.keys(result).length === 0) return undefined;

  return result;
}
